import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Refresh,
} from '@material-ui/icons';
import { isArray } from 'lodash';

import { t } from '~/helpers';
import api from '~/services/api';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1.5),
  },
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    boxShadow: 'none',
  },
  postoRow: {
    cursor: 'pointer',
  },
  iconCell: {
    width: 56,
  },
  totalCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  detailCell: {
    padding: 0,
    background: '#f7f9fb',
  },
  detailContent: {
    padding: theme.spacing(1.5, 3, 2, 7),
  },
  detailTable: {
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1),
  },
}));

const formatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function label(key, fallback) {
  const translated = t(key);

  return translated === key ? fallback : translated;
}

function formatKg(value) {
  return `${formatter.format(Number(value || 0))} kg`;
}

function isSaco(item) {
  return String(item.silotipoareia || '').toUpperCase() !== 'S';
}

function getQuantidadeSacos(item) {
  if (!isSaco(item)) {
    return '';
  }

  const pesoUnitario = Number(item.pesounitariokgtipoareia || 0);

  if (pesoUnitario <= 0) {
    return formatter.format(0);
  }

  return formatter.format(
    Number(item.estoquepostotipoareia || 0) / pesoUnitario
  );
}

export default function EstoqueAreia() {
  const classes = useStyles();
  const [postos, setPostos] = useState([]);
  const [opened, setOpened] = useState({});
  const [loading, setLoading] = useState(false);

  const handleLoad = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.get('estoqueareia/resumo');
      setPostos(Array.isArray(data) ? data : []);
    } catch (erros) {
      if (isArray(erros)) erros.map((e) => toast.error(t(e)));
      else toast.error(t(erros));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleLoad();
  }, [handleLoad]);

  const handleToggle = useCallback((idposto) => {
    setOpened((state) => ({
      ...state,
      [idposto]: !state[idposto],
    }));
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <Button
          color="primary"
          disabled={loading}
          onClick={handleLoad}
          startIcon={<Refresh />}
          variant="outlined"
        >
          {label('s.geral.atualizar', 'Atualizar')}
        </Button>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        {loading && <LinearProgress variant="query" />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.iconCell} />
              <TableCell>
                {label('s.menu.configuracao.posto', 'Posto')}
              </TableCell>
              <TableCell align="right">
                {label('s.operacao.estoqueareia.totalposto', 'Estoque total')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {postos.map((posto) => {
              const isOpened = Boolean(opened[posto.idposto]);
              const itens = posto.itens || [];

              return (
                <React.Fragment key={posto.idposto}>
                  <TableRow
                    hover
                    className={classes.postoRow}
                    onClick={() => handleToggle(posto.idposto)}
                  >
                    <TableCell className={classes.iconCell}>
                      <IconButton
                        size="small"
                        aria-label={isOpened ? 'Fechar' : 'Abrir'}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleToggle(posto.idposto);
                        }}
                      >
                        {isOpened ? (
                          <KeyboardArrowDown />
                        ) : (
                          <KeyboardArrowRight />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>{posto.codigonomeposto}</TableCell>
                    <TableCell align="right" className={classes.totalCell}>
                      {formatKg(posto.estoquetotalareia)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.detailCell} colSpan={3}>
                      <Collapse in={isOpened} timeout="auto" unmountOnExit>
                        <div className={classes.detailContent}>
                          {itens.length > 0 ? (
                            <Table size="small" className={classes.detailTable}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                    {label(
                                      's.cadastro.pitstop.tipoareia',
                                      'Tipo de areia'
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {label(
                                      's.operacao.estoqueareia.origem',
                                      'Origem'
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    Quantidade de sacos
                                  </TableCell>
                                  <TableCell align="right">
                                    {label(
                                      's.operacao.estoqueareia.quantidadekg',
                                      'Quantidade'
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {itens.map((item, index) => (
                                  <TableRow
                                    key={`${posto.idposto}-${item.idtipoareia}-${index}`}
                                  >
                                    <TableCell>
                                      {item.descricaotipoareia}
                                    </TableCell>
                                    <TableCell>
                                      {!isSaco(item)
                                        ? label(
                                            's.operacao.transferenciaareiasilo.silo',
                                            'Silo'
                                          )
                                        : label(
                                            's.operacao.transferenciaareiasilo.saco',
                                            'Areia em saco'
                                          )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {getQuantidadeSacos(item)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatKg(item.estoquepostotipoareia)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography
                              className={classes.empty}
                              variant="body2"
                            >
                              {label(
                                's.operacao.estoqueareia.semestoque',
                                'Sem estoque de areia cadastrado.'
                              )}
                            </Typography>
                          )}
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {!loading && postos.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography className={classes.empty} variant="body2">
                    {label(
                      's.operacao.estoqueareia.semregistros',
                      'Nenhum posto encontrado.'
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
